import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { Flip } from 'gsap/Flip'
import { CustomEase } from 'gsap/CustomEase'
import { lenis } from './lenis.js'

gsap.registerPlugin(ScrollTrigger, Flip, CustomEase)

const MARKERS = false

let mm

const createStatsTimeline = (parent) => {

	const main = parent.querySelector("[data-stats-main]")
	const fill = parent.querySelector("[data-stats-fill]")
	const targets = gsap.utils.toArray("[data-stats-target]")
	const numbers = gsap.utils.toArray("[data-stats-number]")
	const cutouts = gsap.utils.toArray("[data-stats-cutout]")
	const cards = gsap.utils.toArray("[data-stats-card]")
	const images = gsap.utils.toArray("[data-stats-image]")
	const left = parent.querySelector("[data-stats-left]")
	const right = parent.querySelector("[data-stats-right]")
	const image1 = parent.querySelector(".stats__image--1")
	const image2 = parent.querySelector(".stats__image--2")
	const measureCtx = document.createElement('canvas').getContext('2d')

	CustomEase.create("smooth", "M0,0 C0.38,0.005 0.215,1 1,1")


	let lastFillWidth = 0
	let lastFillHeight = 0

	const syncCutouts = () => {
		if (!fill || !cutouts.length) return

		const width = parent.offsetWidth
		const height = parent.offsetHeight

		if (width !== lastFillWidth || height !== lastFillHeight) {
			lastFillWidth = width
			lastFillHeight = height
			fill.setAttribute("viewBox", `0 0 ${width} ${height}`)
			fill.setAttribute("width", String(width))
			fill.setAttribute("height", String(height))
		}

		const parentRect = parent.getBoundingClientRect()

		numbers.forEach((el, i) => {
			const text = cutouts[i]
			if (!text) return

			const style = getComputedStyle(el)
			const rect = el.getBoundingClientRect()
			const content = el.textContent.trim()
			const fontSize = parseFloat(style.fontSize)
			const lineHeight = style.lineHeight === 'normal'
				? fontSize
				: parseFloat(style.lineHeight)
			const letterSpacing = style.letterSpacing === 'normal'
				? 0
				: parseFloat(style.letterSpacing)

			measureCtx.font = `${style.fontWeight} ${fontSize}px "Space Mono", monospace`
			const metrics = measureCtx.measureText(content || '0')
			const ascent = metrics.fontBoundingBoxAscent ?? metrics.actualBoundingBoxAscent ?? fontSize * 0.8
			const descent = metrics.fontBoundingBoxDescent ?? metrics.actualBoundingBoxDescent ?? fontSize * 0.2
			const halfLeading = (lineHeight - ascent - descent) / 2
			const y = rect.top - parentRect.top + halfLeading + ascent

			text.textContent = content
			text.removeAttribute('dominant-baseline')
			text.setAttribute('x', String(rect.left - parentRect.left))
			text.setAttribute('y', String(y))
			text.setAttribute('text-anchor', 'start')
			text.setAttribute('font-family', 'Space Mono, monospace')
			text.setAttribute('font-size', String(fontSize))
			text.setAttribute('font-weight', style.fontWeight)
			text.setAttribute('letter-spacing', String(letterSpacing))
			text.setAttribute('font-kerning', 'none')
		})
	}

	const layout = {
		c1: 9,
		c2: 7,
		leftR1: 0,
		rightR1: 1,
		w1: 1,
		w2: 0.65
	}

	const applyLayout = () => {
		main.style.gridTemplateColumns = `${layout.c1}fr ${layout.c2}fr`
		left.style.gridTemplateRows = `minmax(0, ${layout.leftR1}fr) minmax(0, 1fr)`
		right.style.gridTemplateRows = `minmax(0, ${layout.rightR1}fr) minmax(0, 1fr)`
		if (cards[0]) cards[0].style.setProperty('--stats-w', layout.w1)
		if (cards[1]) cards[1].style.setProperty('--stats-w', layout.w2)
	}

	const hold = (target) => {
		if (!lenis?.isScrolling) return

		lenis.scrollTo(target, {
			duration: 1.2,
			easing: gsap.parseEase('power2.inOut'),
			lock: true
		})
	}

	gsap.set(image1, { scale: 0, transformOrigin: 'bottom right' })
	gsap.set(image2, { scale: 1, transformOrigin: 'bottom left' })
	applyLayout()

	const tl = gsap.timeline({
		scrollTrigger: {
			id: 'stats',
			trigger: main,
			start: 'center center',
			end: '+=100%',
			pin: parent,
			scrub: true,
			invalidateOnRefresh: true,
			onRefresh: syncCutouts,
			onEnter: (self) => hold(self.end - 1),
			onEnterBack: (self) => hold(self.start + 1),
			markers: MARKERS
		},
		onComplete: syncCutouts,
		onReverseComplete: syncCutouts,
		onUpdate: syncCutouts,
		defaults: {
			duration: 1.5,
			ease: 'none'
		}
	})

	tl.to(numbers[0], {
		left: 0,
		fontSize: '4.5rem',
		color: '#191917'
	})

	tl.to(numbers[1], {
		color: 'transparent',
		fontSize: '27.5rem'
	}, 0)

	tl.to(layout, {
		c1: 7,
		c2: 9,
		leftR1: 1,
		rightR1: 0,
		w1: 0.65,
		w2: 1,
		onUpdate: applyLayout
	}, 0)

	tl.to(image1, {
		scale: 1
	}, 0)

	tl.to(image2, {
		scale: 0
	}, 0)

	tl.to({}, { duration: 0.3 })

	document.fonts.ready.then(syncCutouts)

	syncCutouts()

	return () => {
		main.style.removeProperty('grid-template-columns')
		left.style.removeProperty('grid-template-rows')
		right.style.removeProperty('grid-template-rows')
		cards.forEach(card => card.style.removeProperty('--stats-w'))
	}

	// const parent = document.querySelector("[data-stats]")
	// const main = parent.querySelector("[data-stats-main]")
	// const targets = gsap.utils.toArray("[data-stats-target]")
	// const numbers = gsap.utils.toArray("[data-stats-number]")
	// const state = Flip.getState(targets, { props: 'margin,fontSize', absolute: true })

	// parent.classList.add('is-final-state')

	// const flip = Flip.from(state)

	// ScrollTrigger.create({
	// 	trigger: main,
	// 	start: 'center center',
	// 	end: '+=2000px',
	// 	pin: parent,
	// 	scrub: true,
	// 	animation: flip,
	// 	markers: true,
	// })

}

export const initStats = () => {
	mm?.revert()

	const parent = document.querySelector("[data-stats]")

	if (!parent) return

	mm = gsap.matchMedia()

	mm.add({
		isDesktop: '(min-width: 1025px)',
		reduceMotion: '(prefers-reduced-motion: reduce)'
	}, (context) => {
		const { isDesktop, reduceMotion } = context.conditions

		if (!isDesktop || reduceMotion) return

		return createStatsTimeline(parent)
	})
}
